import { ProductType } from "./enums/product-type.enum";
import { ScheduleType } from "./enums/schedule-type.enum";
import { CalendarEvent } from "../event/event.model";
import { BasicQuestion } from "../models/ingredients";
import { ProductSharedSettings } from "./models/product.shared";
import { ProductAdvancedSettings } from "./models/product.advanced";
import { ProductScheduleSettings } from "./models/product.schedule";
import { ProductInventorySettings } from "./models/product.inventory";
import { ProductBuilder } from "./product.builder";
import { ProductBundleSettings } from "./models/product.bundle";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import * as moment from "moment";
import { environment } from "src/environments/environment";
import { CalendarEventBuilder } from "../event/event.builder";
import { ToastService } from "ng-uikit-pro-standard";
import { AuthService } from "../services";

export class Product {
  private readonly _shared: ProductSharedSettings;
  private readonly _advanced: ProductAdvancedSettings;
  private readonly _schedule: ProductScheduleSettings;
  private readonly _inventory: ProductInventorySettings;
  private readonly _bundle: ProductBundleSettings;
  public readonly id?: string;
  public readonly venueId?: string;

  private events$$: Promise<void>;
  public events: CalendarEvent[];

  constructor(builder: ProductBuilder) {
    this.id = builder.id;
    this.venueId = builder.venueId;
    this.events = builder.events || [];
    this._shared = new ProductSharedSettings(builder.shared);
    this._schedule = new ProductScheduleSettings(builder.schedule);
    this._advanced = new ProductAdvancedSettings(builder.advanced);
    this._inventory = new ProductInventorySettings(builder.inventory);
    this._bundle = new ProductBundleSettings(builder.bundle);
  }

  public isPrivate(): boolean {
    return this._shared.isPrivate;
  }

  public async initialize(
    httpClient: HttpClient, 
    authService: AuthService, 
    toast: ToastService,
    begin: Date, 
    end: Date,
  ): Promise<void> {
    let getParams: any = {
        begin: begin.getTime(),
    };
    if (this.scheduleType !== ScheduleType.Continuous) {
        getParams = {
            ...getParams,
            end: end.getTime(),
        }
    }

    const userJWT = await authService.user.getIdToken();

    this.events$$ = httpClient.get<Product[]>(
        `${environment.apiBaseUrl}/api/v1/products/${this.id}/events`, 
        {
            headers: new HttpHeaders().set("Authorization", `${userJWT}`),
            params: {
            id: this.id,
            ...getParams,
            },
        },
      )
      .toPromise()
      .then((eventsResponse) => {
        this.events = eventsResponse.map((eventResponse) => {
            return new CalendarEventBuilder().loadAPIResponse(eventResponse).build();
        });

        return;
      })
      .catch((error) => {
        toast.error("Error fetching events for product.", "Error", {opacity: 1});
        throw(error);
      });
  }
  
  public async refresh(
    httpClient: HttpClient, 
    httpHeaders: HttpHeaders,
    toast: ToastService,
  ): Promise<Product> {
    return httpClient.get<Product>(
        `${environment.apiBaseUrl}/api/v1/products/${this.id}`, 
        {
            headers: httpHeaders,
        }
    ).toPromise()
    .then((productResponse) => {
        return new ProductBuilder().loadAPIResponse(productResponse).build();
    })
    .catch((error) => {
        toast.error("Error refreshing product.", "Error", {opacity: 1});
        throw(error);
    });
  }

  public getAvailablePasses() {
    var nextEvent: CalendarEvent;
    //Base Case. 'N/A'
    if (this.events.length == 0) return null;

    let upcomingEvents = this.events
      .sort((a, b) => (moment(a.date).isBefore(moment(b.date)) ? -1 : 1))
      .filter(event => !event.isPastDate());
    if (upcomingEvents.length == 0) nextEvent = null;
    else nextEvent = upcomingEvents[0];
    if (nextEvent == null) return null;
    if (nextEvent.inventory.max == null) {
      if (this.inventory.max == null) {
        return -1;
      } else {
        return this.inventory.max - nextEvent.sold;
      }
    }
    return nextEvent.inventory.max - nextEvent.sold;
  }

  get shared(): ProductSharedSettings {
    return this._shared;
  }

  get advanced(): ProductAdvancedSettings {
    return this._advanced;
  }

  get schedule(): ProductScheduleSettings {
    return this._schedule;
  }

  get inventory(): ProductInventorySettings {
    return this._inventory;
  }

  get bundle(): ProductBundleSettings {
    return this._bundle;
  }

  get price(): number {
    return this._inventory.price;
  }

  get isActive(): boolean {
    return this._shared.isActive;
  }

  get name(): string {
    return this._shared.name;
  }

  get serviceFee(): number {
    return this._shared.serviceFee;
  }

  get tips(): number[] {
    return this._shared.tips;
  }
  get productType(): ProductType {
    return this._shared.type;
  }
  get productTypeMask(): string {
    return ProductType[this._shared.type];
  }

  get description(): string {
    return this._shared.description;
  }

  get instructions(): string {
    return this._shared.instructions;
  }

  get warning(): string {
    return this._shared.warning;
  }

  get questions(): BasicQuestion[] {
    return this._shared.questions;
  }

  get scheduleType(): ScheduleType {
    return this._schedule.type;
  }

  get scheduleTypeMask(): string {
    return ScheduleType[this._schedule.type];
  }
  get isCustomDaysPass(): boolean {
    return this._schedule.type == ScheduleType.CustomDays;
  }

  toAPIRequest = () => {
    const apiRequest = {
        shared: this._shared.toJSON(),
        schedule: this._schedule ? this._schedule.toJSON() : null,
        advanced: this._advanced ? this._advanced.toJSON() : null,
        inventory: this._inventory ? this._inventory.toJSON() : null,
        customEvents: this.getCustomEvents(),
    };

    if (this._bundle.items && this._bundle.items.length > 0) {
        return {
            ...apiRequest,
            bundle: this._bundle.toJSON(),
        }
    }

    return apiRequest;
  }

  getCustomEvents = (): any[] => {
    return this.events
        .filter(calendarEvent => calendarEvent.isCustomEvent)
        .map(customEvent => customEvent.toAPIRequest());
  }

  compare = (product: Product): number => (this.name < product.name ? -1 : 1);

  get isLineskip(): boolean {
    return this.productType == ProductType.LineSkip;
  }
  get isCover(): boolean {
    return this.productType == ProductType.Cover;
  }
  get isDrink(): boolean {
    return this.productType == ProductType.Drink;
  }
  get isOther(): boolean {
    return this.productType == ProductType.Other;
  }
  get isBundle(): boolean {
    return this.productType == ProductType.Bundle;
  }
}
